import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    model,
    signal,
    untracked
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { Predicate } from 'src/app/pbir/utils/Predicate';
import { VisualComponent } from 'src/app/pbir/visuals/base/Visual.component';
import { VisualDataRequest, VisualDataSource } from 'src/app/pbir/visuals/data';
import { VisualContext } from 'src/app/pbir/visuals/VisualContext';
import { VisualCustomField } from 'src/app/pbir/visuals/VisualCustomField';
import { VisualField } from 'src/app/pbir/visuals/VisualField';
import { VisualFilter } from 'src/app/pbir/visuals/VisualFilter';
import { SharedModule } from 'src/app/shared/shared.module';
import { distinctBy, isNullOrUndefined } from 'src/app/shared/utils';

@Component({
  selector: 'filter-editor__basic',
  template: `
    <div class="visual-editor-box__input">
      <label>Show items when the value is</label>
      <mat-select class="trg-outline user-select-none w-100p h-24"
                  panelClass="trg-outline-dropdown"
                  [value]="predicate().operator"
                  (valueChange)="updatePredicate({ operator: $event })">
        @for (o of operatorOptions; track o.value) {
          <mat-option [value]="o.value" [title]="o.title">
            <span>{{o.title}}</span>
          </mat-option>
        }
      </mat-select>
    </div>

    <div class="visual-editor-box__input" [hidden]="!showSearch()">
      <label>Search</label>
      <input type="text" class="trg-outline w-100p h-24" [(ngModel)]="searchText" />
    </div>
    
    @if (showSearch()) {
      @let opts = options();

      <div class="filter-editor__basic-option-list" [hidden]="!opts?.length">
        @for (o of opts; track o.value) {
          <div class="filter-editor__basic-option">
            <trg-checkbox size="tiny"
                          appearance="square"
                          [value]="o.selected"
                          (valueChange)="toggleValue(o, $event)"
                          [hideBorder]="true"></trg-checkbox>
            <span class="ml-4 text-ellipsis" [title]="o.title">{{o.title}}</span>
          </div>
        }
      </div>

      <div>
        @if (searching()) { 
          <span>Loading...</span>
        }
        @else { 
          @if (searchFailed()) {
            <span>Query resulted in an error.</span>
          }
          @else {
            <span [hidden]="opts.length || !searchSuccess()">
              No values found.
            </span>
          }
        }
      </div>
    }
  `,
  styles: [`
    :host {
      display: contents;
    }

    .filter-editor__basic-option-list {
      max-height: 100px;
      overflow-x: hidden;
      overflow-y: auto;
    }

    .filter-editor__basic-option {
      display: flex;
      align-items: center;
      height: 20px;
      span { user-select: all; }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatSelectModule, FormsModule, SharedModule],
  standalone: true
})
export class BasicFilterComponent {
  readonly context = input<VisualContext>();
  readonly dataSource = input<VisualDataSource>();
  readonly customFields = input<ReadonlyArray<VisualCustomField>>();

  readonly showSearch = computed(() => this.predicate()?.operator != Predicate.BasicOperator.All);

  readonly searchText = model('');
  readonly searchTextDebounced = toSignal(toObservable(this.searchText).pipe(
    map(x => x?.trim().toLowerCase()),
    distinctUntilChanged(),
    debounceTime(400)
  ));
  

  readonly searchRequest = computed(() => {
    let context = this.context();
    let target = this.predicate()?.target;

    if (!context || !target || !target?.isValid || !this.showSearch()) 
      return null;

    return new VisualDataRequest(context.dataModel, {
      debugName: `${this.context().debugName()}.basicFilter([${target.formatLongTitle('.')}])`,
      schema: context.schema,
      fields: [new VisualField(target)],
      filterGroups: [],
      customFields: this.customFields(),
      currency: context.currency(),
      recordPlan: false,
    });
  });

  readonly searchData = new VisualComponent.DefaultData(this.searchRequest, this.dataSource);

  readonly searchResource = computed(() => {
    let data = this.searchData;
 
    let target = this.predicate()?.target;
    if (!target)
      return null;

    let searchText = this.searchTextDebounced();
    
    if (!searchText) {
      return data.rows({
        take: 1000
      });
    } else {
      return data.matchingValues({
        field: { target },
        searchText,
        take: 1000,
      });
    }
  });

  readonly searching = computed(() => !!this.searchRequest() && !!this.searchData.loadingOrFetching());
  readonly searchFailed = computed(() => !!this.searchRequest() && !!this.searchResource()?.failure());
  readonly searchSuccess = computed(() => !!this.searchRequest() && !!this.searchResource()?.success());

  readonly options = computed<ReadonlyArray<OptionModel>>(() => {
    let selectedOptions = [...this.selectedOptions()];
    
    let searchResource = this.searchResource();
    if (!searchResource?.hasResult())
      return selectedOptions;
      
    let target = this.predicate()?.target;
    if (!target)
      return selectedOptions;

    let formatter = this.context()?.dataFormatter();
    if (!formatter)
      return selectedOptions;
     
    let result = searchResource.result();
    if (!result)
      return selectedOptions;

    let i = result.columns.get(target).dataIndex;

    this.setMakeOption();
    let foundOptions = result.rows.map(r => this.makeOption(r[i], false));

    let selectedOptionsSet = new Set<any>(selectedOptions.map(x => x.value));

    let options = distinctBy([
      ...selectedOptions,
      ...foundOptions.filter(o => !selectedOptionsSet.has(o.value))
    ], x => x.value);

    return options;
  });


  readonly selectedOptions = signal<ReadonlyArray<OptionModel>>([]);

  toggleValue(option: OptionModel, selected: boolean) {
    let options = [...this.selectedOptions()];

    if (selected)
      options.push({ ...option, selected: true });
    else
      options = options.filter(x => x.value != option.value);

    this.updatePredicate({
      values: options.map(x => x.value)
    });

    this.selectedOptions.set(options);
  }


  readonly predicate = model<Partial<VisualFilter.BasicPredicate>>({
    operator: Predicate.BasicOperator.All,
    values: []
  });

  readonly predicateValues = computed(() => this.predicate()?.values ?? [], {
    equal: (a, b) => {
      if (isNullOrUndefined(a))
        return isNullOrUndefined(b);

      if (a === b)
        return true;

      if (a.length != b.length)
        return false;

      for (let i = 0; i < a.length; ++i) {
        if (a[i] != b[i])
          return false;
      }

      return true;
    }
  });

  private predicateChanged = effect(() => {
    let _ = [this.predicate(), this.predicateValues(), this.context()?.dataFormatter()];

    untracked(() => {
      let values = this.predicateValues() ?? [];
      this.setMakeOption();
      this.selectedOptions.set(values.map(x => this.makeOption(x, true)));
    });
  });


  updatePredicate(changes: Partial<VisualFilter.BasicPredicate>) {
    let predicate = {
      ...(this.predicate() ?? {}),
      ...changes
    };

    this.predicate.set({
      type: predicate.type,
      target: predicate.target,
      operator: predicate.operator,
      values: predicate.values
    });
  }

  readonly operatorOptions = [
    {
      title: 'Any value',
      value: Predicate.BasicOperator.All
    },
    {
      title: 'One of selected',
      value: Predicate.BasicOperator.OneOf
    },
    {
      title: 'Not one of selected',
      value: Predicate.BasicOperator.NotOneOf
    }
  ];


  private makeOption: (value: any, selected: boolean) => OptionModel;
  private setMakeOption() {
    let target = this.predicate().target;
    let formatter = this.context()?.dataFormatter();
    this.makeOption = (value, selected) => {
      if (!target || !formatter) {
        return {
          value,
          title: '' + value,
          selected: false
        };
      }

      return {
        value: formatter.applyDataFormat(value, {
          dataType: target.dataType,
          isAggregated: target.isAggregated,
          aggregation: target.aggregation,
          format: target.dataFormat
        }),

        title: formatter.formatForDisplay(value, {
          dataType: target.dataType,
          isAggregated: target.isAggregated,
          aggregation: target.aggregation,
          display: target.dataDisplay
        }),

        selected: !!selected
      };
    };
  }
}

interface ValueModel {
  readonly value: any;
  readonly title: any;
}

interface OptionModel extends ValueModel {
  readonly selected: boolean;
}

